import { useState, useEffect, useRef, useCallback } from 'react';
import RegistrationPage   from './dms/RegistrationPage';
import ChangePasswordPage from './dms/ChangePasswordPage';

// ── URL constants ─────────────────────────────────────────────────────────────
// BASE is used for API fetch() calls — proxied by CRA dev server on port 3000
// SPRING_NAV is used for <a href> navigation links — must bypass CRA proxy
const BASE = '/spring';
const SPRING_NAV = window.location.port === '3000' ? 'http://localhost:8080/spring' : '/spring';

// ── API layer ─────────────────────────────────────────────────────────────────

function parseFolderLi(liEl) {
  const span = liEl.querySelector(':scope > span.folder-title');
  const key = span ? (span.getAttribute('data-item') || '') : '';
  const name = key.split('/').filter(Boolean).pop() || key;
  const ul = liEl.querySelector(':scope > ul');
  const files = [];
  const subfolders = [];
  if (ul) {
    ul.querySelectorAll(':scope > li.file').forEach(f => {
      const fk = f.getAttribute('data-item') || '';
      files.push({ key: fk, name: fk.split('/').pop() || fk });
    });
    ul.querySelectorAll(':scope > li.folder').forEach(ch => subfolders.push(parseFolderLi(ch)));
  }
  return { key, name, files, subfolders };
}

async function apiFetchTree() {
  const res = await fetch(`${BASE}/s3/tree`, { credentials: 'include' });
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const inner = doc.querySelector('.inner-div');
  if (!inner) return [];
  const roots = [];
  inner.querySelectorAll('#tree > li.folder').forEach(li => roots.push(parseFolderLi(li)));
  return roots;
}

async function apiPresign(key, disposition = 'inline') {
  const r = await fetch(`${BASE}/s3/presigned-download?key=${encodeURIComponent(key)}&disposition=${disposition}`, { credentials: 'include' });
  return r.json();
}

async function apiPhotos(folderKey) {
  const r = await fetch(`${BASE}/photos?folderKey=${encodeURIComponent(folderKey)}`, { credentials: 'include' });
  return r.json();
}

async function apiDeleteFile(key) {
  const fd = new FormData(); fd.append('key', key);
  const r = await fetch(`${BASE}/s3/delete`, { method: 'POST', credentials: 'include', body: fd });
  return r.text();
}

async function apiDeleteFolder(key) {
  const r = await fetch(`${BASE}/s3/delete-folder?key=${encodeURIComponent(key)}`, { method: 'DELETE', credentials: 'include' });
  return r.text();
}

async function apiRename(oldKey, newName) {
  const r = await fetch(`${BASE}/rename?oldKey=${encodeURIComponent(oldKey)}&newKey=${encodeURIComponent(newName)}`, { credentials: 'include' });
  return r.text();
}

async function apiCreateFolder(folderPath) {
  const fd = new FormData(); fd.append('folderPath', folderPath);
  const r = await fetch(`${BASE}/s3/create-folder`, { method: 'POST', credentials: 'include', body: fd, redirect: 'manual' });
  if (r.type !== 'opaqueredirect' && !r.ok) throw new Error('Create folder failed (status ' + r.status + ')');
}

async function apiMakePublic(key) {
  const fd = new FormData(); fd.append('key', key);
  const r = await fetch(`${BASE}/s3/public`, { method: 'POST', credentials: 'include', body: fd, redirect: 'manual' });
  if (r.type !== 'opaqueredirect' && !r.ok) throw new Error('Make public failed (status ' + r.status + ')');
}

async function apiUpdateDates(path, expiryDate, reminderDate) {
  const r = await fetch(
    `${BASE}/api/s3/update-dates?path=${encodeURIComponent(path)}&expiryDate=${expiryDate}&reminderDate=${reminderDate}`,
    { method: 'POST', credentials: 'include' }
  );
  return r.text();
}

async function apiInitiate(key) {
  const r = await fetch(`${BASE}/s3/initiate?key=${encodeURIComponent(key)}`, { credentials: 'include' });
  return r.json();
}

async function apiPresignPart(key, uploadId, partNumber) {
  const r = await fetch(
    `${BASE}/s3/presign-part?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}&partNumber=${partNumber}`,
    { credentials: 'include' }
  );
  return r.json();
}

async function apiComplete(key, uploadId, parts) {
  const r = await fetch(
    `${BASE}/s3/complete?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}`,
    { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parts) }
  );
  return r.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const IMG_EXTS = new Set(['jpg','jpeg','png','gif','webp','svg','bmp']);
const VID_EXTS = new Set(['mp4','mov','webm','ogg','avi','mkv']);
const AUD_EXTS = new Set(['mp3','wav','m4a','flac']);

function getExt(name) {
  const d = name.lastIndexOf('.'); return d >= 0 ? name.slice(d + 1).toLowerCase() : '';
}

function fileIcon(name) {
  const e = getExt(name);
  if (IMG_EXTS.has(e)) return '🖼️';
  if (VID_EXTS.has(e)) return '🎥';
  if (AUD_EXTS.has(e)) return '🎵';
  if (e === 'pdf') return '📄';
  if (['xls','xlsx','csv'].includes(e)) return '📊';
  if (['doc','docx'].includes(e)) return '📝';
  if (['zip','rar','tar','gz','7z'].includes(e)) return '📦';
  return '📄';
}

function findNode(nodes, key) {
  for (const n of nodes) {
    if (n.key === key) return n;
    const f = findNode(n.subfolders || [], key);
    if (f) return f;
  }
  return null;
}

function pathTo(nodes, targetKey, acc) {
  const path = acc || [];
  for (const n of nodes) {
    if (n.key === targetKey) return [...path, n];
    const p = pathTo(n.subfolders || [], targetKey, [...path, n]);
    if (p) return p;
  }
  return null;
}

function searchFiles(nodes, q) {
  const results = [];
  const walk = (ns) => ns.forEach(n => {
    (n.files || []).forEach(f => {
      if (f.name.toLowerCase().includes(q)) results.push({ ...f, folderName: n.name });
    });
    walk(n.subfolders || []);
  });
  walk(nodes);
  return results;
}

async function retryFn(fn, n = 3, d = 500) {
  let err;
  for (let i = 0; i < n; i++) {
    try { return await fn(); }
    catch (e) { err = e; if (i < n - 1) await new Promise(r => setTimeout(r, d * (i + 1))); }
  }
  throw err;
}

async function concurrent(tasks, max = 4) {
  const res = new Array(tasks.length);
  let idx = 0;
  await Promise.all(Array.from({ length: Math.min(max, tasks.length) }, async () => {
    while (idx < tasks.length) { const j = idx++; res[j] = await tasks[j](); }
  }));
  return res;
}

// ── Folder tree sidebar node ──────────────────────────────────────────────────

function SidebarNode({ node, activeKey, onSelect, expanded, onToggle, depth }) {
  const active = node.key === activeKey;
  const open = expanded[node.key];
  const hasKids = (node.subfolders || []).length > 0;
  return (
    <div>
      <div
        onClick={() => { onSelect(node.key); if (hasKids) onToggle(node.key); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: `5px 8px 5px ${8 + depth * 14}px`,
          cursor: 'pointer', borderRadius: 6, marginBottom: 1, userSelect: 'none',
          background: active ? '#1a73e8' : 'transparent',
          color: active ? '#fff' : 'var(--text-primary, #333)',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-secondary, #f0f0f0)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ fontSize: 9, color: active ? '#cfe2ff' : '#aaa', width: 10, flexShrink: 0 }}>
          {hasKids ? (open ? '▼' : '▶') : ''}
        </span>
        <span>📁</span>
        <span style={{ fontSize: 12, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.name}
        </span>
      </div>
      {open && (node.subfolders || []).map(ch => (
        <SidebarNode key={ch.key} node={ch} activeKey={activeKey}
          onSelect={onSelect} expanded={expanded} onToggle={onToggle} depth={depth + 1} />
      ))}
    </div>
  );
}

// ── Reusable modal ────────────────────────────────────────────────────────────

const inputSt = {
  width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 14,
  border: '1px solid var(--border-color, #ddd)', background: 'var(--bg-secondary, #f8f8f8)',
  color: 'var(--text-primary, #333)', outline: 'none', boxSizing: 'border-box',
};

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg-primary, #fff)', borderRadius: 14, padding: 24, width: '100%', maxWidth: 340, boxShadow: '0 8px 40px rgba(0,0,0,.22)' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 17 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function MFoot({ onCancel, onOk, okLabel = 'OK', okColor = '#1a73e8' }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
      <button onClick={onCancel} style={{ padding: '8px 16px', borderRadius: 7, border: '1px solid #ddd', background: 'none', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
      <button onClick={onOk} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: okColor, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{okLabel}</button>
    </div>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

function SmabizzFooter() {
  return (
    <footer style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 900,
      background: '#002366', borderTop: '2px solid rgba(255,255,255,0.15)',
      textAlign: 'center', padding: '6px 16px',
    }}>
      <p style={{ color: '#9ea7b4', fontFamily: "'Roboto', sans-serif", fontSize: 12, fontWeight: 400, letterSpacing: '.3px', margin: 0 }}>
        &copy; {new Date().getFullYear()} All rights reserved &mdash; SmaBizZ Cloud Products &nbsp;|&nbsp;
        <a href="https://smabizz.com" style={{ color: '#4CCEE8', textDecoration: 'none', fontWeight: 500 }}>smabizz.com</a>
      </p>
    </footer>
  );
}

// ── Left nav menu group ───────────────────────────────────────────────────────

function NavGroup({ icon, label, children, navExpanded, onExpand, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 2 }}>
      <div
        onClick={() => { if (!navExpanded && onExpand) { onExpand(); } else setOpen(o => !o); }}
        style={{
          display: 'flex', alignItems: 'center', gap: navExpanded ? 10 : 0,
          padding: '9px 12px', cursor: 'pointer', borderRadius: 6,
          color: '#c8d3e0', userSelect: 'none',
          justifyContent: navExpanded ? 'flex-start' : 'center',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.08)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        title={!navExpanded ? label : ''}
      >
        <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
        {navExpanded && (
          <>
            <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{label}</span>
            <span style={{ fontSize: 10, color: '#7a8fa6' }}>{open ? '▼' : '▶'}</span>
          </>
        )}
      </div>
      {open && navExpanded && (
        <div style={{ paddingLeft: 12 }}>{children}</div>
      )}
    </div>
  );
}

function NavItem({ icon, label, href, onClick, navExpanded, active }) {
  const base = {
    display: 'flex', alignItems: 'center', gap: navExpanded ? 8 : 0,
    padding: '7px 12px', cursor: 'pointer', borderRadius: 6,
    color: active ? '#fff' : '#a8b8cc',
    background: active ? '#1a73e8' : 'transparent',
    textDecoration: 'none', fontSize: 13, fontWeight: active ? 600 : 400,
    justifyContent: navExpanded ? 'flex-start' : 'center',
    userSelect: 'none',
  };
  const over  = e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.08)'; };
  const out   = e => { if (!active) e.currentTarget.style.background = 'transparent'; };
  const inner = <><span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>{navExpanded && <span>{label}</span>}</>;

  if (href) return <a href={href} style={base} onMouseEnter={over} onMouseLeave={out} title={!navExpanded ? label : ''}>{inner}</a>;
  return <div onClick={onClick} style={base} onMouseEnter={over} onMouseLeave={out} title={!navExpanded ? label : ''}>{inner}</div>;
}

// ── Responsive breakpoints ────────────────────────────────────────────────────

const BP_MOBILE  = 640;
const BP_TABLET  = 1024;
const FOOTER_H   = 34;
const HEADER_H   = 56;

function useBreakpoint() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { isMobile: w < BP_MOBILE, isTablet: w >= BP_MOBILE && w < BP_TABLET, isDesktop: w >= BP_TABLET };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function DocManager({ user: userProp }) {
  const { isMobile, isTablet, isDesktop } = useBreakpoint();

  const [tree,          setTree]          = useState([]);
  const [currentKey,    setCurrentKey]    = useState(null);
  const [expanded,      setExpanded]      = useState({});
  const [view,          setView]          = useState('grid');
  const [loading,       setLoading]       = useState(false);
  const [err,           setErr]           = useState('');
  const [search,        setSearch]        = useState('');
  const [dragging,      setDragging]      = useState(false);

  // Nav sidebar
  const [navExpanded,     setNavExpanded]     = useState(isDesktop);
  const [drawerOpen,      setDrawerOpen]      = useState(false);
  const [activePage,      setActivePage]      = useState('documents'); // 'documents' | 'registration' | 'changepassword'

  // Folder panel visibility on mobile (separate from nav drawer)
  const [folderPanelOpen, setFolderPanelOpen] = useState(false);

  // Modals
  const [ctx,           setCtx]           = useState(null);
  const [newFolderM,    setNewFolderM]    = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameM,       setRenameM]       = useState(null);
  const [expiryM,       setExpiryM]       = useState(null);
  const [gallery,       setGallery]       = useState(null);
  const [uploadM,       setUploadM]       = useState(false);
  const [staged,        setStaged]        = useState([]);
  const [progress,      setProgress]      = useState({});
  const [uploading,     setUploading]     = useState(false);

  // Session user (csrftoken + businessTitle)
  const [sessionUser,   setSessionUser]   = useState(null);

  // Close overlay when going to desktop
  useEffect(() => {
    if (isDesktop) { setDrawerOpen(false); setFolderPanelOpen(false); }
  }, [isDesktop]);

  // Fetch session info
  useEffect(() => {
    fetch(`${BASE}/s3/me`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401) { window.location.href = `${SPRING_NAV}/login`; return null; }
        return r.ok ? r.json() : null;
      })
      .then(u => { if (u) setSessionUser(u); })
      .catch(() => {});
  }, []);

  const displayUser   = sessionUser || userProp || {};
  const username      = displayUser.username      || '';
  const role          = displayUser.role          || (userProp?.role) || '';
  const businessTitle = displayUser.businessTitle || displayUser.businesstitle || userProp?.businesstitle || 'Document Management';
  const csrftoken     = displayUser.csrftoken     || '';
  const isOwner       = role === 'role_owner';

  // ── Load tree
  const loadTree = useCallback(async () => {
    setLoading(true);
    try { setTree(await apiFetchTree()); }
    catch (e) { setErr(e.message || 'Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadTree(); }, [loadTree]);

  useEffect(() => {
    const hide = () => setCtx(null);
    window.addEventListener('click', hide);
    return () => window.removeEventListener('click', hide);
  }, []);

  useEffect(() => {
    if (!gallery) return;
    const h = (e) => {
      if (e.key === 'ArrowRight') setGallery(g => ({ ...g, index: (g.index + 1) % g.images.length }));
      else if (e.key === 'ArrowLeft') setGallery(g => ({ ...g, index: (g.index - 1 + g.images.length) % g.images.length }));
      else if (e.key === 'Escape') setGallery(null);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [gallery]);

  // ── Derived
  const currentNode    = currentKey ? findNode(tree, currentKey) : null;
  const displayFolders = search ? [] : (currentKey ? (currentNode?.subfolders || []) : tree);
  const displayFiles   = search ? searchFiles(tree, search.toLowerCase()) : (currentKey ? (currentNode?.files || []) : []);
  const breadcrumb     = currentKey ? (pathTo(tree, currentKey) || []) : [];
  const totalItems     = displayFolders.length + displayFiles.length;

  // ── Navigation
  const navigateTo = (key) => {
    setCurrentKey(key);
    setSearch('');
    if (key) setExpanded(p => ({ ...p, [key]: true }));
    if (isMobile) setFolderPanelOpen(false);
  };
  const toggleExpand = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const showCtx = (e, item, isFile) => {
    e.preventDefault(); e.stopPropagation();
    setCtx({ x: e.clientX, y: e.clientY, item, isFile });
  };

  // ── Actions
  const doCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    const folderPath = currentKey ? `${currentKey}/${newFolderName.trim()}` : newFolderName.trim();
    try { await apiCreateFolder(folderPath); setNewFolderName(''); setNewFolderM(false); await loadTree(); }
    catch (e) { setErr(e.message); }
  };

  const doDeleteFolder = async (key) => {
    if (!window.confirm(`Delete folder "${key.split('/').pop()}" and all its contents?`)) return;
    try {
      const r = await apiDeleteFolder(key);
      if (r.includes('expired')) { setErr('Session expired'); return; }
      if (currentKey === key || (currentKey || '').startsWith(key + '/')) setCurrentKey(null);
      await loadTree();
    } catch (e) { setErr(e.message); }
  };

  const doDeleteFile = async (key) => {
    if (!window.confirm(`Delete "${key.split('/').pop()}"?`)) return;
    try {
      const r = await apiDeleteFile(key);
      if (r === 'unauthorized') { setErr('Session expired'); return; }
      await loadTree();
    } catch (e) { setErr(e.message); }
  };

  const doView = async (key) => {
    try {
      const { url } = await apiPresign(key, 'inline');
      if (!url) { setErr('Session expired'); return; }
      window.open(url, '_blank');
    } catch (e) { setErr(e.message); }
  };

  const doDownload = async (key) => {
    try {
      const { url } = await apiPresign(key, 'attachment');
      if (!url) { setErr('Session expired'); return; }
      const a = document.createElement('a');
      a.href = url; a.download = ''; document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) { setErr(e.message); }
  };

  const doMakePublic = async (key) => {
    if (!window.confirm('Make this file publicly accessible?')) return;
    try { await apiMakePublic(key); } catch (e) { setErr(e.message); }
  };

  const doRename = async () => {
    if (!renameM?.newName?.trim()) return;
    try { await apiRename(renameM.key, renameM.newName.trim()); setRenameM(null); await loadTree(); }
    catch (e) { setErr(e.message); }
  };

  const doSaveDates = async () => {
    if (!expiryM?.expiryDate || !expiryM?.reminderDate) { setErr('Fill in both dates'); return; }
    try { await apiUpdateDates(expiryM.key, expiryM.expiryDate, expiryM.reminderDate); setExpiryM(null); }
    catch (e) { setErr(e.message); }
  };

  const doViewPhotos = async (key) => {
    try {
      const urls = await apiPhotos(key);
      if (!urls?.length) { setErr('No photos in this folder'); return; }
      setGallery({ images: urls, index: 0 });
    } catch (e) { setErr(e.message); }
  };

  // ── Upload
  const addFiles = (fileList) => {
    const arr = Array.from(fileList);
    setStaged(prev => {
      const seen = new Set(prev.map(f => f.name + '|' + f.size));
      return [...prev, ...arr.filter(f => !seen.has(f.name + '|' + f.size))];
    });
  };

  const pickFiles = () => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.multiple = true;
    inp.style.cssText = 'position:fixed;top:-9999px';
    document.body.appendChild(inp);
    inp.onchange = () => { addFiles(inp.files); document.body.removeChild(inp); };
    inp.click();
  };

  const doUploadAll = async () => {
    if (!staged.length || uploading) return;
    setUploading(true);
    const folder = currentKey || '';
    const initProg = {};
    staged.forEach(f => { initProg[f.name] = { pct: 0, done: false, error: null }; });
    setProgress(initProg);

    let allOk = true;
    for (const file of staged) {
      try {
        setProgress(p => ({ ...p, [file.name]: { pct: 30, done: false, error: null } }));
        const fd = new FormData();
        fd.append('folder', folder);
        fd.append('file', file);
        const res = await fetch(`${BASE}/s3/upload`, {
          method: 'POST', credentials: 'include', body: fd, redirect: 'manual',
        });
        // Spring redirects (302) on success — opaqueredirect means upload succeeded
        if (res.type === 'opaqueredirect' || res.ok) {
          setProgress(p => ({ ...p, [file.name]: { pct: 100, done: true, error: null } }));
        } else {
          throw new Error('Upload failed (status ' + res.status + ')');
        }
      } catch (e) {
        setProgress(p => ({ ...p, [file.name]: { pct: 0, done: false, error: e.message } }));
        allOk = false;
      }
    }
    setUploading(false);
    await loadTree();
    if (allOk) { setStaged([]); setProgress({}); setUploadM(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files); setUploadM(true);
  };

  const galNext = () => setGallery(g => ({ ...g, index: (g.index + 1) % g.images.length }));
  const galPrev = () => setGallery(g => ({ ...g, index: (g.index - 1 + g.images.length) % g.images.length }));

  const ctxMenuItems = ctx ? (ctx.isFile ? [
    { label: '👁️ View / Preview',   action: () => doView(ctx.item.key) },
    { label: '⬇️ Download',          action: () => doDownload(ctx.item.key) },
    { label: '✏️ Rename',            action: () => setRenameM({ key: ctx.item.key, newName: ctx.item.name }) },
    { label: '🌐 Make Public',       action: () => doMakePublic(ctx.item.key) },
    { label: '📅 Expiry & Reminder', action: () => setExpiryM({ key: ctx.item.key, expiryDate: '', reminderDate: '' }) },
    { label: '🗑 Delete File',       action: () => doDeleteFile(ctx.item.key), danger: true },
  ] : [
    { label: '📂 Open Folder',       action: () => navigateTo(ctx.item.key) },
    { label: '⬆️ Upload Here',       action: () => { navigateTo(ctx.item.key); setUploadM(true); setStaged([]); } },
    { label: '📷 View Photos',       action: () => doViewPhotos(ctx.item.key) },
    { label: '📁➕ New Subfolder',    action: () => { navigateTo(ctx.item.key); setNewFolderM(true); } },
    { label: '🗑 Delete Folder',     action: () => doDeleteFolder(ctx.item.key), danger: true },
  ]) : [];

  // ── Nav sidebar width based on breakpoint + state
  const navW = isMobile ? 0 : (navExpanded ? 220 : 56);
  const folderW = isMobile ? 0 : 200;

  // ── Left nav content (shared between sidebar and mobile drawer)
  const NavContent = ({ inDrawer }) => (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: FOOTER_H + 8 }}>
      {/* Logo */}
      <div style={{ padding: '12px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', flexShrink: 0 }}>
        {(inDrawer || navExpanded) ? (
          <img src="https://d3spxlgpyyshwt.cloudfront.net/public_html/images/logo-default-191x52.png"
            alt="SmaBizZ" style={{ height: 30, objectFit: 'contain' }} />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}><span style={{ fontSize: 22 }}>🗂️</span></div>
        )}
      </div>

      {(inDrawer || navExpanded) && (
        <div style={{ padding: '8px 14px 2px', fontSize: 10, color: '#4a6580', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          DMS Menu
        </div>
      )}

      <div style={{ padding: '8px 4px' }}>
        <NavGroup icon="👤" label="Login Accounts" navExpanded={inDrawer || navExpanded}
          onExpand={!inDrawer ? () => setNavExpanded(true) : undefined}>
          {isOwner && (
            <NavItem icon="➕" label="Registration"
              onClick={() => { setActivePage('registration'); if (inDrawer) setDrawerOpen(false); else setNavExpanded(false); }}
              navExpanded={inDrawer || navExpanded}
              active={activePage === 'registration'} />
          )}
          <NavItem icon="🔑" label="Change Password"
            onClick={() => { setActivePage('changepassword'); if (inDrawer) setDrawerOpen(false); else setNavExpanded(false); }}
            navExpanded={inDrawer || navExpanded}
            active={activePage === 'changepassword'} />
        </NavGroup>

        <NavGroup icon="🗂️" label="Documents Mgmt" navExpanded={inDrawer || navExpanded}
          onExpand={!inDrawer ? () => setNavExpanded(true) : undefined}>
          <NavItem icon="📁" label="Documents"
            onClick={() => { setActivePage('documents'); navigateTo(null); if (inDrawer) setDrawerOpen(false); else setNavExpanded(false); }}
            navExpanded={inDrawer || navExpanded}
            active={activePage === 'documents'} />
        </NavGroup>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Inter,system-ui,sans-serif', background: '#f4f6f9' }}>

      {/* ── Top header ──────────────────────────────────────────────── */}
      <div style={{
        height: HEADER_H, flexShrink: 0,
        background: 'linear-gradient(90deg,#1a2a4a 0%,#1e3a5f 100%)',
        display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px',
        boxShadow: '0 2px 8px rgba(0,0,0,.25)', zIndex: 200,
      }}>
        <button
          onClick={() => isMobile ? setDrawerOpen(o => !o) : setNavExpanded(n => !n)}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: '4px 6px', borderRadius: 4, flexShrink: 0, lineHeight: 1 }}
          title="Toggle menu"
        >☰</button>

        <div style={{ flex: 1, textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: 'clamp(12px,2.5vw,17px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 6px' }}>
          {businessTitle}
        </div>

        {username && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a73e8', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
              {username.charAt(0).toUpperCase()}
            </span>
            {!isMobile && (
              <span style={{ color: '#7dffb3', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {username}
              </span>
            )}
          </div>
        )}

        <a
          href={`${SPRING_NAV}/logout${csrftoken ? `?uqid=${csrftoken}` : ''}`}
          style={{ color: '#ff6b6b', fontSize: 22, textDecoration: 'none', padding: '3px 6px', borderRadius: 4, flexShrink: 0, lineHeight: 1 }}
          title="Logout"
        >⏻</a>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

        {/* Mobile overlay backdrop */}
        {isMobile && drawerOpen && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 300 }}
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {/* Mobile folder panel backdrop */}
        {isMobile && folderPanelOpen && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 300 }}
            onClick={() => setFolderPanelOpen(false)}
          />
        )}

        {/* ── Left nav sidebar (desktop / tablet) ─────────────────── */}
        {!isMobile && (
          <aside style={{
            width: navW, flexShrink: 0,
            background: 'linear-gradient(180deg,#1a2a4a 0%,#0e1e33 100%)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            transition: 'width .2s',
          }}>
            <NavContent inDrawer={false} />
          </aside>
        )}

        {/* ── Mobile/tablet nav drawer (overlay) ──────────────────── */}
        {isMobile && (
          <aside style={{
            position: 'fixed', top: HEADER_H, left: 0, bottom: 0, width: 240,
            background: 'linear-gradient(180deg,#1a2a4a 0%,#0e1e33 100%)',
            display: 'flex', flexDirection: 'column', zIndex: 400,
            transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform .25s cubic-bezier(.4,0,.2,1)',
            boxShadow: drawerOpen ? '4px 0 24px rgba(0,0,0,.4)' : 'none',
          }}>
            <NavContent inDrawer={true} />
          </aside>
        )}

        {/* ── Main content: folder tree + file area ────────────────── */}
        <div
          style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}
          onDragOver={e => { if (activePage === 'documents') { e.preventDefault(); setDragging(true); } }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
          onDrop={e => { if (activePage === 'documents') handleDrop(e); }}
        >
          {/* Registration / Change Password pages */}
          {activePage !== 'documents' && (
            <div style={{ flex: 1, overflowY: 'auto', background: '#f4f6f9' }}>
              {activePage === 'registration'   && <RegistrationPage />}
              {activePage === 'changepassword' && <ChangePasswordPage />}
            </div>
          )}

          {activePage === 'documents' && dragging && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,115,232,.12)', border: '3px dashed #1a73e8', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', fontSize: 20, fontWeight: 700, color: '#1a73e8' }}>
              Drop files to upload
            </div>
          )}

          {/* Folder tree panel — desktop/tablet (documents only) */}
          {activePage === 'documents' && !isMobile && (
            <div style={{ width: folderW, flexShrink: 0, borderRight: '1px solid #e2e8f0', overflowY: 'auto', padding: '12px 6px', background: '#fff', paddingBottom: FOOTER_H + 8 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: '#1a73e8', marginBottom: 12, paddingLeft: 8 }}>Folders</div>
              <div
                onClick={() => navigateTo(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: 4, userSelect: 'none', background: !currentKey ? '#1a73e8' : 'transparent', color: !currentKey ? '#fff' : '#333' }}
                onMouseEnter={e => { if (currentKey) e.currentTarget.style.background = '#f0f0f0'; }}
                onMouseLeave={e => { if (currentKey) e.currentTarget.style.background = 'transparent'; }}
              >🏠 All</div>
              {tree.map(n => (
                <SidebarNode key={n.key} node={n} activeKey={currentKey}
                  onSelect={navigateTo} expanded={expanded} onToggle={toggleExpand} depth={0} />
              ))}
            </div>
          )}

          {/* Mobile folder panel — slide from left (documents only) */}
          {activePage === 'documents' && isMobile && folderPanelOpen && (
            <div style={{ position: 'fixed', top: HEADER_H, left: 0, bottom: FOOTER_H, width: 260, background: '#fff', zIndex: 400, boxShadow: '4px 0 20px rgba(0,0,0,.2)', overflowY: 'auto', padding: '12px 8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1a73e8' }}>Folders</span>
                <button onClick={() => setFolderPanelOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
              </div>
              <div
                onClick={() => navigateTo(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 6, background: !currentKey ? '#1a73e8' : '#f5f5f5', color: !currentKey ? '#fff' : '#333' }}
              >🏠 All Folders</div>
              {tree.map(n => (
                <SidebarNode key={n.key} node={n} activeKey={currentKey}
                  onSelect={navigateTo} expanded={expanded} onToggle={toggleExpand} depth={0} />
              ))}
            </div>
          )}

          {/* ── File area (documents page only) ──────────────────────── */}
          {activePage === 'documents' && <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

            {/* Toolbar */}
            <div style={{ padding: isMobile ? '8px 10px' : '10px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, flexWrap: 'wrap', background: '#fff', flexShrink: 0 }}>

              {/* Mobile folder toggle button */}
              {isMobile && (
                <button onClick={() => setFolderPanelOpen(o => !o)}
                  style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: folderPanelOpen ? '#1a73e8' : '#fff', color: folderPanelOpen ? '#fff' : '#333', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  📁 Folders
                </button>
              )}

              {/* Breadcrumb */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <span onClick={() => navigateTo(null)} style={{ cursor: 'pointer', color: '#1a73e8', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>Docs</span>
                {breadcrumb.map((c, i) => (
                  <span key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <span style={{ color: '#718096', flexShrink: 0 }}>›</span>
                    <span onClick={() => navigateTo(c.key)}
                      style={{ cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? 80 : 140, color: i === breadcrumb.length - 1 ? '#333' : '#1a73e8', display: 'block' }}>
                      {c.name}
                    </span>
                  </span>
                ))}
              </div>

              {/* Search — full on desktop, icon-triggered on mobile */}
              {!isMobile && (
                <input value={search} placeholder="Search files…" onChange={e => setSearch(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 20, padding: '5px 12px', fontSize: 13, width: isTablet ? 140 : 180, background: '#f8f8f8', outline: 'none' }} />
              )}

              {isMobile && (
                <input value={search} placeholder="Search…" onChange={e => setSearch(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 20, padding: '5px 10px', fontSize: 12, flex: 1, minWidth: 0, background: '#f8f8f8', outline: 'none' }} />
              )}

              {/* New folder — icon only on mobile */}
              <button onClick={() => setNewFolderM(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: isMobile ? '5px 8px' : '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>
                📁{!isMobile && <span> New</span>}
              </button>

              <button onClick={() => { setUploadM(true); setStaged([]); }}
                style={{ padding: isMobile ? '5px 10px' : '6px 12px', borderRadius: 6, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                ⬆{!isMobile && ' Upload'}
              </button>

              <button onClick={loadTree} title="Refresh"
                style={{ padding: isMobile ? '5px 8px' : '6px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>
                🔄
              </button>

              <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                {[['grid','⊞'],['list','☰']].map(([v, icon]) => (
                  <button key={v} onClick={() => setView(v)}
                    style={{ padding: isMobile ? '5px 8px' : '5px 9px', border: 'none', cursor: 'pointer', fontSize: 14, background: view === v ? '#1a73e8' : '#f8f8f8', color: view === v ? '#fff' : '#333' }}>
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            {/* Error banner */}
            {err && (
              <div style={{ margin: '6px 12px', padding: '7px 12px', borderRadius: 8, fontSize: 13, background: '#fff5f5', border: '1px solid #feb2b2', color: '#c53030', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                {err}
                <button onClick={() => setErr('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c53030', fontSize: 16, marginLeft: 8 }}>✕</button>
              </div>
            )}

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 10 : 14, paddingBottom: FOOTER_H + 10 }}>
              {loading && !totalItems && <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div>}

              {search && (
                <div style={{ marginBottom: 10, fontSize: 13, color: '#718096' }}>
                  {displayFiles.length} result(s) for "{search}"
                  <button onClick={() => setSearch('')} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', fontSize: 12 }}>Clear</button>
                </div>
              )}

              {!loading && totalItems === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>
                  <div style={{ fontSize: 48, marginBottom: 10 }}>📂</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {search ? 'No files found' : currentKey ? 'This folder is empty' : 'No folders yet'}
                  </div>
                  {!search && <div style={{ fontSize: 13, marginTop: 6 }}>Upload files or create a folder to get started</div>}
                </div>
              )}

              {/* Grid view */}
              {view === 'grid' && !loading && totalItems > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 100 : 120}px, 1fr))`, gap: isMobile ? 8 : 10 }}>
                  {displayFolders.map(folder => (
                    <div key={folder.key}
                      onDoubleClick={() => !isMobile && navigateTo(folder.key)}
                      onClick={() => isMobile && navigateTo(folder.key)}
                      onContextMenu={e => showCtx(e, folder, false)}
                      style={{ padding: isMobile ? '10px 6px' : '12px 8px', borderRadius: 10, border: '1px solid #e2e8f0', cursor: 'pointer', textAlign: 'center', background: '#fff', userSelect: 'none' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,.10)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{ fontSize: isMobile ? 36 : 42, marginBottom: 6 }}>📁</div>
                      <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={folder.name}>{folder.name}</div>
                      <div style={{ fontSize: 10, color: '#718096', marginTop: 2 }}>
                        {(folder.files?.length || 0)} files{folder.subfolders?.length ? ` · ${folder.subfolders.length} folders` : ''}
                      </div>
                    </div>
                  ))}
                  {displayFiles.map(file => (
                    <div key={file.key}
                      onClick={() => doView(file.key)}
                      onContextMenu={e => showCtx(e, file, true)}
                      style={{ padding: isMobile ? '10px 6px' : '12px 8px', borderRadius: 10, border: '1px solid #e2e8f0', cursor: 'pointer', textAlign: 'center', background: '#fff', userSelect: 'none' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,.10)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{ fontSize: isMobile ? 36 : 42, marginBottom: 6 }}>{fileIcon(file.name)}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>{file.name}</div>
                      {search && file.folderName && <div style={{ fontSize: 10, color: '#718096', marginTop: 2 }}>in {file.folderName}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* List view */}
              {view === 'list' && !loading && totalItems > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? 12 : 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      {['Name', 'Info', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#718096', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayFolders.map(folder => (
                      <tr key={folder.key}
                        onDoubleClick={() => !isMobile && navigateTo(folder.key)}
                        onClick={() => isMobile && navigateTo(folder.key)}
                        onContextMenu={e => showCtx(e, folder, false)}
                        style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f8f8'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '8px 8px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>📁 <strong>{folder.name}</strong></span>
                        </td>
                        <td style={{ padding: '8px 8px', color: '#718096', fontSize: 11 }}>
                          {!isMobile && `${(folder.files?.length || 0)} files${folder.subfolders?.length ? `, ${folder.subfolders.length} sub` : ''}`}
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                          <button onClick={e => { e.stopPropagation(); showCtx(e, folder, false); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#718096' }}>⋮</button>
                        </td>
                      </tr>
                    ))}
                    {displayFiles.map(file => (
                      <tr key={file.key}
                        onClick={() => doView(file.key)}
                        onContextMenu={e => showCtx(e, file, true)}
                        style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8f8f8'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ padding: '8px 8px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{fileIcon(file.name)} <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? 140 : '100%' }}>{file.name}</span></span>
                        </td>
                        <td style={{ padding: '8px 8px', color: '#718096', fontSize: 11 }}>
                          {search && file.folderName ? `in ${file.folderName}` : ''}
                        </td>
                        <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                          <button onClick={e => { e.stopPropagation(); showCtx(e, file, true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#718096' }}>⋮</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>}

          {/* ── Context menu ─────────────────────────────────────────── */}
          {activePage === 'documents' && ctx && (
            <div onClick={e => e.stopPropagation()}
              style={{ position: 'fixed', top: Math.min(ctx.y, window.innerHeight - 280), left: Math.min(ctx.x, window.innerWidth - 210), zIndex: 1000, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.15)', minWidth: 200, overflow: 'hidden' }}>
              {ctxMenuItems.map((item, i) => (
                <button key={i} onClick={() => { item.action(); setCtx(null); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: item.danger ? '#e53e3e' : '#333' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8f8f8'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >{item.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <SmabizzFooter />

      {/* ── New Folder Modal ─────────────────────────────────────────── */}
      {newFolderM && (
        <Modal title="New Folder" onClose={() => { setNewFolderM(false); setNewFolderName(''); }}>
          {currentKey && <div style={{ fontSize: 12, color: '#718096', marginBottom: 8 }}>In: {currentKey.split('/').pop()}</div>}
          <input autoFocus value={newFolderName} placeholder="Folder name"
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') doCreateFolder(); if (e.key === 'Escape') setNewFolderM(false); }}
            style={inputSt} />
          <MFoot onCancel={() => { setNewFolderM(false); setNewFolderName(''); }} onOk={doCreateFolder} okLabel="Create" />
        </Modal>
      )}

      {/* ── Rename Modal ─────────────────────────────────────────────── */}
      {renameM && (
        <Modal title="Rename" onClose={() => setRenameM(null)}>
          <input autoFocus value={renameM.newName}
            onChange={e => setRenameM(r => ({ ...r, newName: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') doRename(); if (e.key === 'Escape') setRenameM(null); }}
            style={inputSt} />
          <MFoot onCancel={() => setRenameM(null)} onOk={doRename} okLabel="Save" />
        </Modal>
      )}

      {/* ── Expiry & Reminder Modal ──────────────────────────────────── */}
      {expiryM && (
        <Modal title="Expiry & Reminder" onClose={() => setExpiryM(null)}>
          <div style={{ fontSize: 12, color: '#718096', marginBottom: 12, wordBreak: 'break-all' }}>{expiryM.key.split('/').pop()}</div>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Expiry Date</label>
          <input type="date" value={expiryM.expiryDate}
            onChange={e => setExpiryM(x => ({ ...x, expiryDate: e.target.value }))}
            style={{ ...inputSt, marginBottom: 12 }} />
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Reminder Date</label>
          <input type="date" value={expiryM.reminderDate}
            onChange={e => setExpiryM(x => ({ ...x, reminderDate: e.target.value }))}
            style={inputSt} />
          <MFoot onCancel={() => setExpiryM(null)} onOk={doSaveDates} okLabel="Save" />
        </Modal>
      )}

      {/* ── Upload Modal ─────────────────────────────────────────────── */}
      {uploadM && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 560, borderRadius: '16px 16px 0 0', padding: '18px 16px 24px', boxShadow: '0 -4px 24px rgba(0,0,0,.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#1a73e8' }}>⬆ Upload Files</span>
              <button onClick={() => { if (!uploading) { setUploadM(false); setStaged([]); } }}
                style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer' }}>×</button>
            </div>
            {currentKey && <div style={{ fontSize: 12, color: '#718096', marginBottom: 6 }}>To: {currentKey.split('/').pop()}</div>}
            {!currentKey && <div style={{ fontSize: 12, color: '#e56b00', marginBottom: 6 }}>Tip: Open a folder first to organise files.</div>}
            <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{staged.length} file{staged.length !== 1 ? 's' : ''} ready</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', maxHeight: 200, overflowY: 'auto' }}>
              {staged.length === 0 ? (
                <li style={{ color: '#888', fontSize: 13, padding: '6px 0' }}>No files selected yet.</li>
              ) : staged.map((f, i) => {
                const p = progress[f.name];
                return (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{f.size > 1048576 ? (f.size/1048576).toFixed(1)+'MB' : Math.round(f.size/1024)+'KB'}</div>
                      {p && !p.done && !p.error && (
                        <div style={{ marginTop: 4, background: '#e0e0e0', borderRadius: 4, height: 4 }}>
                          <div style={{ width: p.pct+'%', background: '#1a73e8', borderRadius: 4, height: '100%', transition: 'width .2s' }} />
                        </div>
                      )}
                      {p?.done && <span style={{ fontSize: 11, color: '#22c55e' }}>✓ Done</span>}
                      {p?.error && <span style={{ fontSize: 11, color: '#e53e3e' }}>{p.error}</span>}
                    </div>
                    {!uploading && (
                      <button onClick={() => setStaged(arr => arr.filter((_, j) => j !== i))}
                        style={{ background: 'none', border: 'none', color: '#cc0000', fontSize: 18, cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}>✕</button>
                    )}
                  </li>
                );
              })}
            </ul>
            {!uploading && (
              <button onClick={pickFiles}
                style={{ width: '100%', padding: 13, marginBottom: 10, background: '#f0f6ff', color: '#1a73e8', border: '2px dashed #1a73e8', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                ＋ Add Files
              </button>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { if (!uploading) { setUploadM(false); setStaged([]); } }}
                style={{ flex: 1, padding: 13, background: '#f5f5f5', color: '#555', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: uploading ? .5 : 1 }}>
                Cancel
              </button>
              <button onClick={doUploadAll} disabled={staged.length === 0 || uploading}
                style={{ flex: 2, padding: 13, background: '#00CC66', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: (staged.length === 0 || uploading) ? .5 : 1 }}>
                {uploading ? '⏳ Uploading…' : '⬆ Upload All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Photo Gallery ─────────────────────────────────────────────── */}
      {gallery && (
        <div onClick={e => { if (e.target === e.currentTarget) setGallery(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.9)', zIndex: 1500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <button onClick={() => setGallery(null)}
            style={{ position: 'fixed', top: 14, right: 16, background: 'rgba(0,0,0,.5)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, cursor: 'pointer', zIndex: 1600 }}>×</button>
          <button onClick={galPrev}
            style={{ position: 'fixed', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,.18)', border: '2px solid rgba(255,255,255,.5)', color: '#fff', fontSize: '1.8em', width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', zIndex: 1600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
          <img src={gallery.images[gallery.index]} alt="" style={{ maxWidth: '95vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 10, display: 'block' }} />
          <div style={{ color: '#ddd', fontSize: 12, textAlign: 'center', marginTop: 10, maxWidth: '90vw', wordBreak: 'break-all', padding: '0 60px' }}>
            {gallery.index + 1} / {gallery.images.length} — {decodeURIComponent(gallery.images[gallery.index].split('/').pop().split('?')[0])}
          </div>
          <button onClick={galNext}
            style={{ position: 'fixed', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,.18)', border: '2px solid rgba(255,255,255,.5)', color: '#fff', fontSize: '1.8em', width: 48, height: 48, borderRadius: '50%', cursor: 'pointer', zIndex: 1600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
        </div>
      )}
    </div>
  );
}
